/**
 * Dependency health, derived from what the TRANSPORT observed (#1790).
 *
 * SLM-MOONBASE #124: a deployment's PostgREST was stopped for ten days. The
 * console signed people in, rendered, and showed two unrelated error panels
 * while nothing anywhere named the dependency that was down.
 *
 * ── why this is DERIVED and never fetched ───────────────────────────────────
 * The obvious design is a health endpoint the shell polls. It fails in exactly
 * the case it exists for: during that incident the console could not reach
 * PostgREST, so it could not have read a verdict stored behind PostgREST
 * either. A monitor that works only while nothing is wrong is not a monitor.
 *
 * ── and why it observes the TRANSPORT, not the query cache ──────────────────
 * The first version of this module watched TanStack's `queryCache`. An
 * adversarial review killed that design with one observation: a `queryFn`
 * written `try { … } catch { return null }` swallows a 503 and RESOLVES, so the
 * cache reports a success that never happened and a degraded region clears
 * itself while the service is still down. There are six such handlers in this
 * repo — `apps/console/src/data/queries.ts:81`, `:106`, `:928` and
 * `apps/portal/src/data.tsx:273`, `:287`, `:2234`. The retry button made it
 * worse: it refetched exactly those queries, so pressing it dismissed the
 * warning rather than testing it.
 *
 * (An earlier version of this comment said `useNavigation` "polls every 60
 * seconds". It does not. `queries.ts:74-90` sets `staleTime: 60_000` and no
 * `refetchInterval`, and `client.tsx` sets `refetchOnWindowFocus: false`, so it
 * issues at most one request per mount. `staleTime` was misread as
 * `refetchInterval` and the claim was repeated in four files. The swallowing is
 * real and is the load-bearing half; the polling was not.)
 *
 * The cache sees what the application DECIDED. Only the transport sees what the
 * server DID. Observing there fixes three further things for free: writes are
 * covered, attribution stops being a guess, and the observation happens before
 * any caller can swallow it.
 *
 * ── what it deliberately does not do ────────────────────────────────────────
 * No timers, no polling, no probing. It observes traffic the application was
 * making anyway, so a quiet app asserts nothing — which is correct: not knowing
 * is not the same as being down.
 */

/**
 * The query focus-recovery defaults, as ONE named value (#2018).
 *
 * Focus recovery is the net that catches an invalidation nobody wrote: a
 * family that was missed, a write from another tab, somebody else's change.
 *
 * The two fields are ONE policy for that reason: refetching on focus with no
 * stale window refetches everything on every alt-tab, and a stale window with
 * no focus refetch never recovers. Bounded at 30s it is a net, not a poll;
 * there is deliberately no refetchInterval. Retry remains host-owned because
 * Console fixture/live and Portal live transports have different contracts.
 */
export const QUERY_FOCUS_RECOVERY_DEFAULTS = {
  staleTime: 30_000,
  refetchOnWindowFocus: true,
} as const

/**
 * The dependencies a person can be told about, in the words they would use.
 *
 * **Only what something actually observes.** An earlier draft named four —
 * data, sign-in, files, realtime — and three were unreachable: the hosts had no
 * way to attribute a failure to them, `packages/auth` issues no observed
 * requests at all, and the multi-dependency sentence was dead code. A named
 * dependency that can never be reported is a promise the product does not keep,
 * so this list grows only when a transport is wired to report it.
 *
 * Being a single member today is why `degradationMessage` names one dependency
 * and why nothing here claims to de-duplicate a set it cannot have.
 */
export type DependencyId = 'data'

export interface Dependency {
  readonly id: DependencyId
  /** Sentence-case, for a region a customer may also read. */
  readonly label: string
}

const DEPENDENCIES: Readonly<Record<DependencyId, Dependency>> = {
  data: { id: 'data', label: 'the data service' },
}

/** What the transport saw on the wire. `status: null` means no response at all. */
export interface Outcome {
  readonly ok: boolean
  readonly status: number | null
}

export type FailureKind =
  | 'unreachable'       // no response at all — the network, or this device
  | 'dependency-down'   // 502/503/504 — the gateway answered, the service did not
  | 'request'           // anything else the server said: this request, not the service

/**
 * What a single outcome means for the SERVICE, which is a different question
 * from what it means for the request.
 *
 * `dependency-down` is the arm that did not exist before this work: 500 and 503
 * were one `error`, so a data plane stopped for ten days read exactly like one
 * bad function. The gateway triplet is the platform's own rule
 * (`packages/cli/src/distribution.ts:172`): those three mean the thing behind
 * the gateway did not answer at all.
 */
export function classifyOutcome(outcome: Outcome): FailureKind | null {
  if (outcome.ok) return null
  if (outcome.status === null) return 'unreachable'
  if (outcome.status === 502 || outcome.status === 503 || outcome.status === 504) return 'dependency-down'
  return 'request'
}

export interface Degradation {
  /** The dependency that is not answering, when one is. */
  readonly unavailable: readonly Dependency[]
  /** No response at all: different words, because it is a different fault. */
  readonly unreachable: boolean
}

const freeze = (state: Degradation): Degradation => Object.freeze({
  unavailable: Object.freeze(state.unavailable) as readonly Dependency[],
  unreachable: state.unreachable,
})

export const HEALTHY: Degradation = freeze({ unavailable: [], unreachable: false })

/**
 * How many consecutive successes clear a raised region.
 *
 * Not one. Raising was debounced and clearing was not, which an adversarial
 * review showed is its own defect rather than an asymmetry in the user's
 * favour: "consecutive" is measured in global COMPLETION order across every
 * in-flight request, so a partial outage — connection-pool exhaustion behind
 * Kong is the realistic one — has successes and failures interleaving, and a
 * single success dropped the bar between two failures. Measured on eight
 * parallel requests, the region appeared and vanished inside one burst. A
 * full-width region that flaps shifts the layout and re-announces to a screen
 * reader every time.
 */
const CLEAR_AFTER = 2

/**
 * The default number of consecutive failed responses before the whole
 * application says something, for a host that does not retry.
 *
 * One is too few: a single background 504 — a gateway timeout on one slow RPC,
 * or a rolling restart — would raise a full-width region above every page and
 * drop it on the next request.
 *
 * A host that retries must NOT use this number; see `raiseAfterForRetry`.
 */
export const RAISE_AFTER_NO_RETRY = 2

/**
 * The threshold for a host whose query client retries, expressed in the host's
 * OWN `retry` setting so the two cannot drift.
 *
 * This exists because the constant above was silently defeated in production.
 * Both hosts set `retry: 1` (`apps/console/src/data/client.tsx:107`,
 * `apps/portal/src/data.tsx:97`), and TanStack's `retry: 1` means TWO attempts —
 * both of which go through the transport and both of which are reported. So one
 * slow RPC's 504, the exact example the threshold was written to tolerate,
 * produced two consecutive failures and raised the region. The guard read as
 * careful and did not exist.
 *
 * `retry + 2` is the smallest threshold one retried query cannot reach on its
 * own: with `retry: 1` a single query contributes at most 2 failures, so 3
 * means at least two distinct queries failed — which is what "the service is
 * not answering" should mean.
 */
export function raiseAfterForRetry(retry: number): number {
  return Math.max(0, Math.trunc(retry)) + 2
}

/**
 * The observed state of the deployment's dependencies.
 *
 * Recovery is the half that is easy to forget and the half a person notices: a
 * region that never clears teaches people to ignore regions. **Traffic that
 * works is what clears it** — not a timer, not a dismiss button, which is the
 * same evidence a person would use.
 */
export class DependencyHealth {
  readonly #raiseAfter: number
  #failures = new Map<DependencyId, number>()
  #successes = 0
  #down = new Set<DependencyId>()
  #unreachable = 0
  #listeners = new Set<(state: Degradation) => void>()
  /**
   * The state object, CACHED — a correctness requirement, not an optimisation.
   *
   * `useSyncExternalStore` compares snapshots with `Object.is`. A `state()` that
   * allocated a fresh object each call is never equal to itself, so React
   * re-renders, re-reads, finds another new object, and loops until it throws
   * `Maximum update depth exceeded`. Measured under React 19.2.7 at 55 renders:
   * the shell died on first paint, on every route, in production too.
   */
  #state: Degradation = HEALTHY

  constructor(options: { raiseAfter?: number } = {}) {
    this.#raiseAfter = Math.max(1, Math.trunc(options.raiseAfter ?? RAISE_AFTER_NO_RETRY))
  }

  /** One wire outcome for one dependency. */
  observe(dependency: DependencyId, outcome: Outcome): void {
    const kind = classifyOutcome(outcome)

    /* ANY answer at all — a 200, a 403, a 503 — proves this device can reach
       the deployment, so it ends the unreachable run whatever else it means.

       Without this the module reproduced its own stated failure mode pointing
       the other way, and there was no way out of it. A wifi blip left
       `#unreachable` at 2; every subsequent ANSWERED 503 took the `request` or
       `dependency-down` path, which never touched the counter; and
       `degradationMessage` checks `unreachable` FIRST. So during SLM-MOONBASE
       #124 itself — the deployment down, the device fine — the region would
       have told the operator it was their own network, permanently, until
       reload. Measured: 50 answered 503s, then 500 answered 401s, and the
       sentence never changed. */
    if (outcome.status !== null) this.#unreachable = 0

    if (kind === null) {
      this.#failures.set(dependency, 0)
      this.#successes += 1
      if (this.#successes >= CLEAR_AFTER) this.#down.clear()
    } else if (kind === 'unreachable') {
      this.#successes = 0
      this.#unreachable += 1
    } else if (kind === 'dependency-down') {
      this.#successes = 0
      const next = (this.#failures.get(dependency) ?? 0) + 1
      this.#failures.set(dependency, next)
      if (next >= this.#raiseAfter) this.#down.add(dependency)
    }
    /* `request` — a refusal, a 404, a 500 from one bad function — is left
       alone deliberately. The REQUEST failed and the service is answering;
       saying otherwise would put a region over the whole application because
       one page asked for something it may not have. It is not evidence of
       recovery either, so it does not count toward clearing. */

    this.#emit()
  }

  /** The cached object. Same contents means the same reference, which React needs. */
  state(): Degradation { return this.#state }

  subscribe(listener: (state: Degradation) => void): () => void {
    this.#listeners.add(listener)
    return () => { this.#listeners.delete(listener) }
  }

  #compute(): Degradation {
    const down = this.#down.has('data')
    /* Frozen like `HEALTHY`, and for the same reason. Only `HEALTHY` used to be:
       a review pointed out that after the first request every state React and
       both hosts actually hold was a plain mutable object, so the freeze test
       covered the one value that stops being live once anything happens. */
    return freeze({
      unavailable: down ? [DEPENDENCIES.data] : [],
      unreachable: this.#unreachable >= this.#raiseAfter,
    })
  }

  /** Recompute, and notify ONLY if the answer actually moved. */
  #emit(): void {
    const next = this.#compute()
    if (same(this.#state, next)) return
    this.#state = next
    for (const listener of this.#listeners) listener(next)
  }
}

/**
 * The transport-free external-store seam consumed by application shells.
 *
 * Hosts deliberately get their own instance: transport observations and query
 * caches belong to one deployable. The mechanics do not. Bound functions are
 * part of the contract because both transports and React pass these callbacks
 * around without their owning object.
 */
export interface DependencyHealthStore {
  readonly observe: (dependency: DependencyId, outcome: Outcome) => void
  readonly subscribe: (listener: () => void) => () => void
  readonly getSnapshot: () => Degradation
}

export function createDependencyHealthStore(
  options: { retries: number },
): DependencyHealthStore {
  const health = new DependencyHealth({ raiseAfter: raiseAfterForRetry(options.retries) })
  return Object.freeze({
    observe: (dependency: DependencyId, outcome: Outcome) => { health.observe(dependency, outcome) },
    subscribe: (listener: () => void) => health.subscribe(listener),
    getSnapshot: () => health.state(),
  })
}

/** Contents, not identity — the one place a new identity is created. */
function same(a: Degradation, b: Degradation): boolean {
  return a.unreachable === b.unreachable
    && a.unavailable.length === b.unavailable.length
    && a.unavailable.every((dependency, index) => dependency.id === b.unavailable[index]?.id)
}

/**
 * The sentence a host shows. One region, one sentence, naming the dependency.
 *
 * Not "Something went wrong". The finding of #124 is that four surfaces each
 * said something true and useless while none said which service was down.
 */
export function degradationMessage(state: Degradation): string | null {
  /* Checked FIRST, and safe to check first only because an answered response
     now ends an unreachable run: telling someone whose wifi dropped that "the
     data service is not responding" blames the deployment for the device, and
     the inverse — telling an operator their own network is at fault while the
     deployment is down — is the failure this ordering used to cause. */
  if (state.unreachable) {
    return 'This device cannot reach the deployment. Recent changes may not have been saved.'
  }
  const [first] = state.unavailable
  if (first === undefined) return null
  const named = first.label.charAt(0).toUpperCase() + first.label.slice(1)
  return `${named} is not responding. What you can see may be out of date.`
}
